import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaSearch,
  FaAd,
  FaUsers,
  FaPen,
  FaEnvelope,
  FaCode,
  FaHandshake,
  FaStar,
  FaChartLine,
  FaRocket,
  FaMobile,
  FaVideo,
  FaPalette,
  FaShoppingCart,
  FaMapMarkerAlt,
  FaBullhorn,
  FaSms,
  FaRobot,
  FaFilter,
  FaUserPlus,
  FaArrowRight,
} from "react-icons/fa";

const services = [
  {
    title: "Search Engine Optimization (SEO)",
    description:
      "Improve your website’s visibility in search engines like Google, increasing organic traffic and brand authority.",
    details:
      "We build data-backed SEO strategies that improve rankings, attract high-intent traffic, and help your business become the obvious choice for your target audience.",
    icon: <FaSearch />,
  },
  {
    title: "Search Engine Marketing (SEM) / Google Ads",
    description:
      "Drive targeted traffic to your site instantly with paid advertising campaigns, maximizing ROI and reach.",
    details:
      "Our paid media teams create efficient Google Ads campaigns focused on conversion rate, lower cost-per-acquisition, and scalable growth across your most valuable channels.",
    icon: <FaAd />,
  },
  {
    title: "Social Media Marketing (SMM)",
    description:
      "Engage users and grow your brand on platforms such as Facebook, Instagram, and LinkedIn with tailored content and campaigns.",
    details:
      "We create intentional social strategies that build brand trust, increase engagement, and turn platform attention into measurable leads and revenue.",
    icon: <FaUsers />,
  },
  {
    title: "Content Marketing",
    description:
      "Create compelling content strategies that attract, educate, and convert your target audience.",
    details:
      "From campaign planning to content creation, we design a content engine that improves authority, nurtures leads, and supports long-term growth.",
    icon: <FaPen />,
  },
  {
    title: "Email Marketing & Automation",
    description:
      "Automate your outreach and nurture leads with personalized email campaigns that drive conversions.",
    details:
      "Our email systems help you stay top-of-mind, segment audiences effectively, and automate follow-ups that turn interest into action.",
    icon: <FaEnvelope />,
  },
  {
    title: "Website Design & Development",
    description:
      "Get a modern, responsive website designed to convert visitors into customers and reflect your brand.",
    details:
      "We create clean, conversion-friendly websites that improve trust, communicate value faster, and give users a smoother journey from first click to final action.",
    icon: <FaCode />,
  },
  {
    title: "Affiliate Marketing & Influencer Outreach",
    description:
      "Expand your reach by partnering with top affiliates and influencers in your niche.",
    details:
      "We help you build strategic partnerships that extend your reach, attract better-quality leads, and create trust through authentic advocacy.",
    icon: <FaHandshake />,
  },
  {
    title: "Online Reputation Management (ORM)",
    description:
      "Monitor and improve your online reputation, ensuring your brand is positively perceived.",
    details:
      "We help protect and elevate your digital reputation through review management, positive engagement, and strategic brand perception work.",
    icon: <FaStar />,
  },
  {
    title: "Analytics & Reporting",
    description:
      "Receive actionable insights and transparent reports to track your marketing performance.",
    details:
      "Clear reporting keeps your marketing focused. We monitor the right metrics, highlight what works, and recommend the next best move for growth.",
    icon: <FaChartLine />,
  },
  {
    title: "Conversion Rate Optimization (CRO)",
    description:
      "Boost sales and leads by refining user experience and optimizing your conversion funnel.",
    details:
      "We improve the journey from interest to action by testing pages, messaging, and user flows to increase conversions without increasing ad spend.",
    icon: <FaRocket />,
  },
  {
    title: "App Store Optimization (ASO)",
    description:
      "Maximize app downloads and visibility in app stores with proven ASO techniques.",
    details:
      "We optimize app metadata, creative assets, and keyword strategy so your app gets discovered and downloaded by the right users.",
    icon: <FaMobile />,
  },
  {
    title: "Video Marketing",
    description:
      "Harness the power of video to engage, educate, and convert your audience.",
    details:
      "Video is one of the strongest tools for attention and trust. We design campaigns that communicate your value quickly and memorably.",
    icon: <FaVideo />,
  },
  {
    title: "Branding & Creative Services",
    description:
      "Get impactful branding, logos, and creative assets that set you apart in the market.",
    details:
      "We craft branding and creative assets that help your audience recognize, trust, and remember your business from the very first impression.",
    icon: <FaPalette />,
  },
  {
    title: "E-commerce Marketing",
    description:
      "Accelerate online sales and grow your e-commerce business with targeted strategies.",
    details:
      "We improve traffic quality, campaign efficiency, and customer journey performance so your online store scales with confidence.",
    icon: <FaShoppingCart />,
  },
  {
    title: "Local SEO & Google My Business",
    description:
      "Be found by local customers and stand out in your community with optimized local SEO.",
    details:
      "Our local SEO approach helps nearby customers discover your business, trust your brand, and take action when they need you most.",
    icon: <FaMapMarkerAlt />,
  },
  {
    title: "Display Ads & Programmatic Advertising",
    description:
      "Reach your audience wherever they are online with highly targeted display ad campaigns.",
    details:
      "We use remarketing and audience-based strategies to keep your brand visible to high-intent prospects across the digital ecosystem.",
    icon: <FaBullhorn />,
  },
  {
    title: "WhatsApp & SMS Marketing",
    description:
      "Connect instantly with customers using direct WhatsApp and SMS campaigns.",
    details:
      "Direct messaging helps brands increase response rates, recover lost leads, and create more personalized customer experiences.",
    icon: <FaSms />,
  },
  {
    title: "Marketing Automation Setup",
    description:
      "Streamline your marketing efforts with advanced automation tools and workflows.",
    details:
      "We implement automation systems that reduce repetitive work, improve response time, and keep your funnel moving without friction.",
    icon: <FaRobot />,
  },
  {
    title: "Funnel Building",
    description:
      "Design and implement marketing funnels that move leads smoothly toward conversion.",
    details:
      "From traffic capture to nurturing and conversion, we build integrated funnels that guide your prospects through each step efficiently.",
    icon: <FaFilter />,
  },
  {
    title: "Lead Generation Campaigns",
    description:
      "Generate high-quality leads for your business using proven, scalable campaigns.",
    details:
      "We design lead-generation systems that target the right audience, improve conversion quality, and support your sales pipeline consistently.",
    icon: <FaUserPlus />,
  },
];

const categories = ["All services", "Performance", "Creative", "Conversion", "Foundation"];

const categoryByTitle = {
  "Search Engine Optimization (SEO)": "Performance",
  "Search Engine Marketing (SEM) / Google Ads": "Performance",
  "Social Media Marketing (SMM)": "Creative",
  "Content Marketing": "Creative",
  "Email Marketing & Automation": "Creative",
  "Website Design & Development": "Foundation",
  "Affiliate Marketing & Influencer Outreach": "Creative",
  "Online Reputation Management (ORM)": "Foundation",
  "Analytics & Reporting": "Performance",
  "Conversion Rate Optimization (CRO)": "Conversion",
  "App Store Optimization (ASO)": "Performance",
  "Video Marketing": "Creative",
  "Branding & Creative Services": "Creative",
  "E-commerce Marketing": "Conversion",
  "Local SEO & Google My Business": "Performance",
  "Display Ads & Programmatic Advertising": "Performance",
  "WhatsApp & SMS Marketing": "Conversion",
  "Marketing Automation Setup": "Foundation",
  "Funnel Building": "Conversion",
  "Lead Generation Campaigns": "Conversion",
};

const Services = () => {
  const [showAll, setShowAll] = useState(false);
  const [modal, setModal] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All services");

  const filteredServices = activeCategory === "All services"
    ? services
    : services.filter((service) => categoryByTitle[service.title] === activeCategory);
  const displayedServices = showAll ? filteredServices : filteredServices.slice(0, 6);

  return (
    <section id="services" className="relative overflow-hidden bg-primary-dark py-20 text-white">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-secondary/70 to-transparent" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.24em] text-secondary-light">
              What we do
            </p>
            <h2 className="text-3xl font-extrabold leading-tight text-white md:text-4xl">
              Growth solutions built around your next move
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-6 text-slate-300 md:text-base">
              From getting discovered to turning more visits into customers, find the right support for your goals.
            </p>
          </div>
          <p className="text-sm font-semibold text-slate-400">20 ways to grow your business</p>
        </div>

        <div className="mb-8 flex gap-2 overflow-x-auto pb-2" role="group" aria-label="Filter services by category">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              aria-pressed={activeCategory === category}
              onClick={() => {
                setActiveCategory(category);
                setShowAll(false);
              }}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition ${
                activeCategory === category
                  ? "border-secondary bg-secondary text-primary-dark"
                  : "border-white/15 bg-white/[0.04] text-slate-300 hover:border-secondary/60 hover:text-white"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {displayedServices.map((service, idx) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: Math.min(idx * 0.04, 0.2) }}
              whileHover={{ y: -4 }}
              className="group flex min-h-[270px] flex-col items-start rounded-xl border border-white/10 bg-white/[0.045] p-6 transition-colors hover:border-secondary/50 hover:bg-white/[0.075]"
            >
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg border border-secondary/20 bg-secondary/10 text-xl text-secondary-light transition group-hover:border-secondary/40 group-hover:bg-secondary/15">
                {service.icon}
              </div>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-secondary-light">
                {categoryByTitle[service.title]}
              </p>
              <h3 className="mb-3 text-lg font-bold leading-snug text-white">
                {service.title}
              </h3>
              <p className="mb-6 text-sm leading-6 text-slate-300">{service.description}</p>
              <motion.button
                whileTap={{ scale: 0.97 }}
                className="mt-auto inline-flex items-center gap-2 text-sm font-bold text-white transition hover:text-secondary-light"
                onClick={() => setModal(service)}
              >
                Explore service <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
              </motion.button>
            </motion.div>
          ))}
        </div>

        {filteredServices.length > 6 && (
          <div className="mt-8 flex justify-center">
            <motion.button
              whileTap={{ scale: 0.95 }}
              className="rounded-full border border-white/15 bg-white/[0.04] px-5 py-2.5 text-sm font-semibold text-white transition hover:border-secondary/50 hover:text-secondary-light"
              onClick={() => setShowAll((current) => !current)}
            >
              {showAll ? "Show fewer services" : "View all services"}
            </motion.button>
          </div>
        )}

        {modal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
            onClick={() => setModal(null)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="service-dialog-title"
              className="relative w-full max-w-lg rounded-xl border border-white/10 bg-primary p-7 shadow-2xl md:p-9"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                aria-label="Close service details"
                className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-xl text-slate-300 transition hover:border-secondary/50 hover:text-white"
                onClick={() => setModal(null)}
              >
                ×
              </button>
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-secondary/10 text-xl text-secondary-light">{modal.icon}</div>
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-secondary-light">
                {categoryByTitle[modal.title]}
              </p>
              <h3 id="service-dialog-title" className="mb-3 pr-8 text-2xl font-bold text-white">{modal.title}</h3>
              <p className="leading-7 text-slate-300">{modal.details || modal.description}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;
